import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { DocumentViewerType } from '@app/origami-api/constants/document-viewer-type';
import {
  DocumentManagerService,
  FileInfo,
  GetTopicFileViewerUrlParams,
  TopicFileViewerInfo,
} from '@app/origami-api/services/document-manager.service';
import { SessionService } from '@app/session/session.service';
import { catchError, map } from 'rxjs/operators';
import { DialogService } from '@app/services/dialogs/dialog.service';
import { ErrorCode } from '@app/origami-api/constants/error-codes';
import { tiStringsFormat } from '@app/shared/pipes/string-format.pipe';
import { Topic } from '@app/origami-api/utils/topic-utils';
import { cloneDeep } from 'lodash';

export interface GetDocumentViewerUrlParams {
  siteId: number;
  fileInfo: FileInfo;
  document?: Topic;
  paramsAddition?: { [prop: string]: string };
  viewerType?: DocumentViewerType;
  promptDownload?: boolean;
  silentErrorCodes?: ErrorCode[];
}

interface GetCompareViewerUrlParams {
  siteId: number;
  fileInfos: FileInfo[];
  paramsAddition?: { [prop: string]: string };
}

export interface CompareViewerValidationError {
  ErrorCode: number;
  ErrorMessage: string;
}

@Injectable()
export class DocumentViewerUrlService {
  constructor(
    private readonly documentManagerService: DocumentManagerService,
    private readonly sessionService: SessionService,
    private readonly dialogService: DialogService,
  ) {}

  getDocumentViewerInfo(params: GetDocumentViewerUrlParams): Observable<TopicFileViewerInfo> {
    return this.getGenericViewerInfo({
      fileInfos: [params.fileInfo],
      paramAddition: this.getParamAddition(params.paramsAddition),
      promptDownload: params.promptDownload,
      siteId: params.siteId,
      viewerType: params.viewerType || DocumentViewerType.ARender,
      silentErrorCodes: params?.silentErrorCodes || [],
    }) as Observable<TopicFileViewerInfo>;
  }

  getCompareViewerInfo(params: GetCompareViewerUrlParams): Observable<TopicFileViewerInfo> {
    return this.getCompareViewerInfoInternal(params) as Observable<TopicFileViewerInfo>;
  }

  getCompareViewerInfoWithValidation(
    params: GetCompareViewerUrlParams,
  ): Observable<TopicFileViewerInfo | CompareViewerValidationError> {
    return this.getCompareViewerInfoInternal(params);
  }

  private getCompareViewerInfoInternal(
    params: GetCompareViewerUrlParams,
  ): Observable<TopicFileViewerInfo | CompareViewerValidationError> {
    const paramsAddition = Object.assign({ 'visualization.multiView.doComparison': 'true' }, params.paramsAddition);

    return this.getGenericViewerInfo(
      {
        fileInfos: params.fileInfos,
        paramAddition: this.getParamAddition(paramsAddition),
        promptDownload: false,
        siteId: params.siteId,
        viewerType: DocumentViewerType.ARenderCompareMode,
      },
      true,
    );
  }

  private getGenericViewerInfo(
    params: GetTopicFileViewerUrlParams,
    isCompare = false,
  ): Observable<TopicFileViewerInfo | CompareViewerValidationError> {
    return this.getTopicFileViewerInfo(params).pipe(
      catchError((err) => {
        switch (err.ErrorCode) {
          case ErrorCode.TOPIC_LOCKED_BY_ANOTHER_USER:
            if (err.Result.LockedUserName) {
              this.dialogService.showWarningModal(
                tiStrings.tiComponents.documents.documentViewer.noDocumentLoaded,
                tiStringsFormat(
                  tiStrings.tiComponents.documents.documentViewer.lockedDocumentPattern,
                  err.Result.LockedUserName,
                ),
              );
            }

            break;
          case ErrorCode.FINISHED_WITH_MINOR_ERRORS:
            if (isCompare) {
              const errorRecords = cloneDeep(err);
              errorRecords.Result.ErrorRecords = errorRecords.Result.ErrorRecords.filter(
                (r) =>
                  ![ErrorCode.ATTACHMENT_HAS_RESTRICTED_CONTENT, ErrorCode.DOCUMENT_IS_INFECTED_WITH_VIRUS].includes(
                    r.ErrorCode,
                  ),
              );

              if (errorRecords.Result.ErrorRecords.length) {
                this.dialogService.showErrors({
                  response: errorRecords,
                });
              }
            } else {
              this.dialogService.showErrors({
                response: err,
              });
            }

            break;
          case ErrorCode.INCORRECT_INPUT_PARAMETERS:
            if (isCompare) {
              return of({ ErrorCode: err.ErrorCode, ErrorMessage: err.ErrorMessage });
            }
            this.dialogService.showErrors({
              response: err,
            });
            break;
        }

        if (err.ErrorCode === ErrorCode.TOPIC_LOCKED_BY_ANOTHER_USER) {
          return of({ LockedUserName: err.Result.LockedUserName });
        }
        return isCompare ? of(err.Result) : of(err);
      }),
    );
  }

  private getParamAddition(params = {}): string {
    const localeParam = `locale=${this.sessionService.locale}`;
    const paramsString = Object.keys(params).reduce(
      (acc: string, paramName: string) => acc + '&' + paramName + '=' + params[paramName],
      '',
    );
    return localeParam + paramsString;
  }

  private getTopicFileViewerInfo(params: GetTopicFileViewerUrlParams): Observable<TopicFileViewerInfo> {
    return this.documentManagerService.getTopicFileViewerInfo(params).pipe(
      map((res) => {
        if (res.ErrorCode !== 0) {
          throw res;
        }
        return res.Result;
      }),
    );
  }
}
